<script setup>
// ✅ PATCH: adicionando ref, watch e nextTick para gerenciar foco
import { ref, watch, nextTick, onMounted, onBeforeUnmount } from "vue";

import { toTitleCase } from "@/utils/stringUtils";
import { formatDateTimeForDisplay } from "@/utils/dateUtils";

const props = defineProps({
  show: { type: Boolean, required: true },
  title: { type: String, default: "Já existe autor com esse nome" },
  message: { type: String, default: "" },
  existingAuthors: { type: Array, default: () => [] },
  isSubmitting: { type: Boolean, default: false },
});

const emit = defineEmits(["close", "confirm", "view-author"]);

// ✅ PATCH: ref para o botão "Confirmar"
const confirmButtonRef = ref(null);


function normalizeUserName(name) {
  return (name ?? "").toString().trim().toLowerCase();
}

function isCreatedBySys(author) {
  return normalizeUserName(author?.createdByUserName) === "sys";
}

function closeModal() {
  if (props.isSubmitting) return;
  emit("close");
}

function confirm() {
  if (props.isSubmitting) return;
  emit("confirm");
}

function openCard(authorId) {
  emit("view-author", authorId);
}

function handleEscKey(event) {
  if (!props.show) return;
  if (event.key === "Escape") closeModal();
}

// ✅ PATCH: função para focar o botão "Confirmar" quando o modal abrir
function focusConfirmButton() {
  // espera o DOM do modal ser renderizado
  nextTick(() => {
    // dois frames para garantir que o Teleport e o layout assentaram
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const btn = confirmButtonRef.value;
        if (btn && typeof btn.focus === "function") {
          btn.focus();
        }
      });
    });
  });
}

// ✅ PATCH: quando o modal passar a ser exibido, foca no botão "Confirmar"
watch(
  () => props.show,
  (isVisible) => {
    if (isVisible) {
      focusConfirmButton();
    }
  }
);


onMounted(() => document.addEventListener("keydown", handleEscKey));
onBeforeUnmount(() => document.removeEventListener("keydown", handleEscKey));
</script>

<template>
  <div v-if="show" class="dup-backdrop" >
    <div class="dup-panel">
      <div class="dup-header">
        <h3 class="dup-title">{{ title }}</h3>
        <button
          type="button"
          class="dup-close-btn"
          :disabled="isSubmitting"
          @click="closeModal"
          aria-label="Fechar"
        >
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path
              fill-rule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clip-rule="evenodd"
            />
          </svg>
        </button>
      </div>

      <div class="dup-body">
        <p class="dup-message">
          {{
            message ||
            "Encontramos autores já cadastrados com esse mesmo nome. Confira antes de prosseguir."
          }}
        </p>

        <div class="dup-list">
          <div
            v-for="a in existingAuthors"
            :key="a.authorId"
            class="dup-row"
            @click="openCard(a.authorId)"
            role="button"
            tabindex="0"
            :title="'Ver detalhes do autor ID ' + a.authorId"
          >
            <img
              v-if="a.createdByUserPhotoPath"
              class="dup-avatar"
              :src="a.createdByUserPhotoPath"
              alt=""
              @error="$event.target.style.display = 'none'"
            />

            <div class="dup-main">
              <div class="dup-top">
                <div class="dup-name truncate">
                  {{ toTitleCase(a.authorName) }}
                </div>

                <span
                  v-if="isCreatedBySys(a)"
                  class="sys-badge"
                  title="Criado pelo Sys"
                >
                  SYS
                </span>
              </div>

              <div class="dup-meta">
                <span class="truncate">
                  Criador: {{ toTitleCase(a.createdByUserName) }}
                </span>
                <span class="dup-dot">•</span>
                <span class="truncate">
                  Atualizado: {{ formatDateTimeForDisplay(a.updatedAt) }}
                </span>
              </div>

              <!-- ✅ Sempre mostra a linha de descrição, com fallback -->
              <div class="dup-desc">
                <span class="dup-desc-label">Descrição:</span>
                <span
                  class="truncate"
                  :title="(a.description || '').trim() || '(sem descrição)'"
                >
                  {{
                    (a.description || "").trim()
                      ? a.description
                      : "(sem descrição)"
                  }}
                </span>
              </div>
            </div>

            <div class="dup-id">
              #{{ a.authorId }}
            </div>
          </div>
        </div>

        <!-- ✅ Este texto fica sempre visível enquanto só a lista rola -->
        <p class="dup-hint">
          Se ainda assim você quiser criar/renomear, confirme abaixo.
        </p>
      </div>

      <div class="dup-footer">
        <button
          type="button"
          class="btn btn-secondary"
          :disabled="isSubmitting"
          @click="closeModal"
        >
          Voltar
        </button>

        <!-- ✅ PATCH: adicionando ref="confirmButtonRef" para controlar o foco -->
<button
  type="button"
  class="btn btn-primary"
  :disabled="isSubmitting"
  @click="confirm"
  ref="confirmButtonRef"
>
  <svg
    v-if="isSubmitting"
    class="w-5 h-5 mr-2 animate-spin"
    viewBox="0 0 24 24"
    fill="none"
  >
    <circle
      class="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      stroke-width="4"
    />
    <path
      class="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
    />
  </svg>
  Confirmar
</button>

      </div>
    </div>
  </div>
</template>

<style scoped>
/* BACKDROP */
.dup-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1200;
  background-color: rgba(0, 0, 0, 0.25);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 1.25rem;
  overflow-y: auto; /* rolagem da página quando o modal é maior que a viewport */
}

/* PAINEL DO MODAL – rolagem própria + altura máxima */
.dup-panel {
  width: 100%;
  max-width: 32rem;              /* 🔽 mais estreito */
  max-height: 90vh;              /* 🔥 altura máxima do modal */
  background-color: var(--bg-content);
  border-radius: 16px;
  overflow: hidden;              /* ✅ rolagem do próprio modal */
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.16);
  display: flex;
  flex-direction: column;
}

/* HEADER */
.dup-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 1rem;
  border-bottom: 1px solid var(--border-color);
  background-color: var(--bg-header);
}

.dup-title {
  font-size: 1.05rem;
  font-weight: 600;
  color: var(--text-dark);
  margin: 0;
}

.dup-close-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.35rem;
  border-radius: 10px;
  color: var(--text-subtle);
  transition: all 0.2s ease-in-out;
}
.dup-close-btn:hover:enabled {
  background-color: var(--neutral-200);
  color: var(--text-dark);
}
.dup-close-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* BODY – sem rolagem própria, quem rola é o painel e a lista */
.dup-body {
  padding: 1rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.dup-message {
  font-size: 0.92rem;
  color: var(--text-dark);
  margin: 0;
  opacity: 0.9;
}

/* LISTA – rolagem própria */
.dup-list {
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  max-height: 50vh;          /* 🔥 segunda barra de rolagem (da lista) */
  overflow-y: auto;
  padding-right: 4px;        /* afasta a barra do conteúdo */
}

/* CARD DA LISTA */
.dup-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 12px;
  padding: 0.65rem 0.75rem;
  cursor: pointer;
  transition: all 0.15s ease-in-out;
  background: rgba(255, 255, 255, 0.55);
}
.dup-row:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08);
  background: rgba(255, 255, 255, 0.85);
}

.dup-avatar {
  width: 34px;
  height: 34px;
  border-radius: 999px;
  object-fit: cover;
  flex-shrink: 0;
  border: 1px solid rgba(0, 0, 0, 0.06);
}

.dup-main {
  flex: 1;
  min-width: 0;
}

.dup-top {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
}

.dup-name {
  font-weight: 700;
  color: var(--text-dark);
  font-size: 0.9rem;
}

.dup-meta {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.78rem;
  opacity: 0.85;
  color: var(--text-dark);
  margin-top: 0.15rem;
  min-width: 0;
}

.dup-dot {
  opacity: 0.6;
}

/* DESCRIÇÃO – sempre visível */
.dup-desc {
  margin-top: 0.2rem;
  display: flex;
  gap: 0.35rem;
  font-size: 0.78rem;
  min-width: 0;
  opacity: 0.9;
}
.dup-desc-label {
  font-weight: 700;
  flex-shrink: 0;
}

.dup-id {
  font-size: 0.75rem;
  opacity: 0.75;
  color: var(--text-dark);
  flex-shrink: 0;
}

/* TEXTO FIXO (não rola com a lista) */
.dup-hint {
  font-size: 0.82rem;
  opacity: 0.85;
  margin: 0.2rem 0 0;
}

.truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* FOOTER */
.dup-footer {
  padding: 0.75rem 1.5rem;
  border-top: 1px solid var(--border-color);
  background-color: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(5px);
  display: flex;
  justify-content: flex-end;
  gap: 0.6rem;
}

</style>
